import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useTutorial } from '../contexts/TutorialContext';
import { ALL_TUTORIALS, TutorialDefinition, getTutorialsByCategory } from '../config/tutorials';
import './TutorialLibrary.css';

interface TutorialProgress {
  tutorialKey: string;
  status: string;
  lastCompletedStep: number;
  totalSteps: number;
  completedAt?: string;
}

const TutorialLibrary: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { startTutorial, getAllProgress } = useTutorial();
  const [tutorialProgress, setTutorialProgress] = useState<TutorialProgress[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [filteredTutorials, setFilteredTutorials] = useState<TutorialDefinition[]>(ALL_TUTORIALS);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const loadProgress = async () => {
      const progress = await getAllProgress();
      logger.debug('Tutorial Library - Progress loaded:', progress);
      logger.debug('Tutorial Library - Progress count:', progress?.length);
      setTutorialProgress(progress);
    };
    loadProgress();
  }, [getAllProgress]);

  useEffect(() => {
    let filtered = ALL_TUTORIALS;

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => {
        const progress = tutorialProgress.find(p => p.tutorialKey === t.key);
        logger.debug(`Tutorial ${t.key} - Progress:`, progress);
        if (statusFilter === 'completed') {
          return progress?.status === 'Completed';
        } else if (statusFilter === 'in-progress') {
          return progress?.status === 'InProgress';
        } else if (statusFilter === 'not-started') {
          return !progress || progress.status === 'NotStarted';
        }
        return true;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query)
      );
    }

    setFilteredTutorials(filtered);
  }, [searchQuery, categoryFilter, statusFilter, tutorialProgress]);

  const getTutorialStatus = (tutorialKey: string): TutorialProgress | undefined => {
    return tutorialProgress.find(p => p.tutorialKey === tutorialKey);
  };

  const getStatusBadge = (tutorialKey: string) => {
    const progress = getTutorialStatus(tutorialKey);
    if (!progress || progress.status === 'NotStarted') {
      return <span className="tl-badge tl-badge--muted">Not Started</span>;
    } else if (progress.status === 'InProgress') {
      const stepsCompleted = progress.lastCompletedStep + 1;
      return <span className="tl-badge tl-badge--amber">In Progress ({stepsCompleted}/{progress.totalSteps})</span>;
    } else if (progress.status === 'Completed') {
      return <span className="tl-badge tl-badge--green">✓ Completed</span>;
    } else if (progress.status === 'Skipped') {
      return <span className="tl-badge tl-badge--muted">Skipped</span>;
    }
    return null;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'general': return '📖';
      case 'combat': return '⚔️';
      case 'progression': return '🌳';
      case 'exploration': return '🗺️';
      case 'crafting': return '⚒️';
      default: return '❓';
    }
  };

  const handleStartTutorial = (tutorialKey: string) => {
    startTutorial(tutorialKey);
    onClose();
  };

  const completedCount = tutorialProgress.filter(p => p.status === 'Completed').length;
  const completionPercentage = Math.round((completedCount / ALL_TUTORIALS.length) * 100);

  return (
    <div className="tutorial-library-overlay">
      <div className="tutorial-library-modal">
        <div className="tutorial-library-header">
          <h2>Tutorial Library</h2>
          <span className="tl-close-btn" onClick={onClose} role="button" tabIndex={0}>×</span>
        </div>

        <div className="tutorial-stats">
          <div className="stat-item">
            <span className="stat-label">Total</span>
            <span className="stat-value">{ALL_TUTORIALS.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Completed</span>
            <span className="stat-value">{completedCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Progress</span>
            <span className="stat-value">{completionPercentage}%</span>
          </div>
          <div className="tl-progress-bar">
            <div className="tl-progress-fill" style={{ width: `${completionPercentage}%` }} />
          </div>
        </div>

        <div className="tutorial-filters">
          <input
            type="text"
            placeholder="Search tutorials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="tl-search-input"
          />

          <div className="filter-group">
            <label>Category:</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              <option value="general">General</option>
              <option value="combat">Combat</option>
              <option value="progression">Progression</option>
              <option value="exploration">Exploration</option>
              <option value="crafting">Crafting</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="not-started">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="tutorial-list">
          {filteredTutorials.length === 0 ? (
            <div className="no-tutorials">
              <p>No tutorials match your search criteria.</p>
            </div>
          ) : (
            filteredTutorials.map(tutorial => {
              const progress = getTutorialStatus(tutorial.key);
              const isCompleted = progress?.status === 'Completed';

              return (
                <div key={tutorial.key} className={`tutorial-card ${isCompleted ? 'completed' : ''}`}>
                  <div className="tutorial-card-header">
                    <div className="tutorial-title-row">
                      <span className="tutorial-icon">{tutorial.icon}</span>
                      <h3>{tutorial.title}</h3>
                      {getStatusBadge(tutorial.key)}
                    </div>
                    <div className="tutorial-meta">
                      <span className="tl-meta-chip tl-meta-chip--category">
                        {getCategoryIcon(tutorial.category)} {tutorial.category}
                      </span>
                      <span className="tl-meta-chip">
                        {tutorial.priority}
                      </span>
                      <span className="tl-meta-chip tl-meta-chip--duration">
                        {tutorial.estimatedMinutes} min
                      </span>
                    </div>
                  </div>

                  <p className="tutorial-description">{tutorial.description}</p>

                  <div className="tutorial-card-footer">
                    <div className="tutorial-info">
                      <span>{tutorial.steps.length} steps</span>
                      {tutorial.prerequisites && tutorial.prerequisites.length > 0 && (
                        <span>Prerequisites: {tutorial.prerequisites.length}</span>
                      )}
                    </div>
                    <span
                      className="tl-start-btn"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleStartTutorial(tutorial.key)}
                    >
                      {isCompleted ? 'Replay' : progress?.status === 'InProgress' ? 'Continue' : 'Start'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialLibrary;
